// src/lib/fotoWarteschlange.js
// --- FOTOS AUCH OHNE NETZ ------------------------------------------------------
// Anna-Lena 15.09.2026 (Vorschlag 10): Im Trafohaeuschen ohne Empfang ging
// "Foto" bisher nicht - der Monteur musste sich merken, was er spaeter
// nachreicht. Jetzt wie bei den Antworten: erst merken, dann senden.
//
//   1. Das Foto wird verkleinert (geht ohne Netz) und SOFORT im Geraet
//      abgelegt - in IndexedDB, weil localStorage fuer Bilder zu klein ist.
//   2. Gesendet wird, sobald Netz da ist (App-Start, "online", alle 60 s,
//      beim Aufnehmen, Speichern-Knopf, Abschluss). Die Foto-ID entsteht im
//      Geraet; ein zweiter Versuch nach einer verlorenen Antwort legt nichts
//      doppelt an (Datei "gibt es schon" und Eintrag 23505 zaehlen als erledigt).
//   3. Lehnt der Server wirklich ab, bleibt das Foto im Geraet und wird rot
//      angezeigt - nie stillschweigend weg. Zeitueberschreitung und fehlendes
//      Netz sind KEINE Ablehnung (gleiche Regel wie istNetzproblem).
//
// Eigene Datenbank ("etb_fotos_v1"), damit die gemerkte Anmeldung in
// lib/offlineDB.js unberuehrt bleibt.

import { supabase, withTimeout } from './supabase';
import { istNetzproblem } from './warteschlange';

const DB_NAME = 'etb_fotos_v1';
const STORE = 'warten';
const EREIGNIS = 'etb-fotos-geaendert';

let verbindung = null;
function db() {
  if (verbindung) return verbindung;
  verbindung = new Promise((ok, fehler) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      const d = req.result;
      if (!d.objectStoreNames.contains(STORE)) d.createObjectStore(STORE, { keyPath: 'id' });
    };
    req.onsuccess = () => {
      const d = req.result;
      // iOS schliesst die Verbindung im Hintergrund gern - dann neu oeffnen.
      d.onclose = () => { verbindung = null; };
      d.onversionchange = () => { try { d.close(); } catch { /* egal */ } verbindung = null; };
      ok(d);
    };
    req.onerror = () => fehler(req.error);
  });
  verbindung.catch(() => { verbindung = null; });
  return verbindung;
}

// Eine Transaktion. Alle Anfragen werden synchron in "arbeit" gestellt (kein
// await darin, sonst schliesst IndexedDB die Transaktion). Scheitert sie
// (z. B. Verbindung verloren), wird die Verbindung neu geoeffnet und EINMAL
// wiederholt.
async function tx(modus, arbeit, nochmal = true) {
  try {
    // Zeitgrenze: Meldet sich IndexedDB nie (bekannt bei iOS nach dem
    // Hintergrund), hingen sonst Speichern-Knopf und Foto-Upload fuer immer.
    const d = await Promise.race([db(), new Promise((_, f) => setTimeout(() => f(new Error('Gerätespeicher antwortet nicht')), 8000))]);
    return await new Promise((ok, fehler) => {
      const zeit = setTimeout(() => fehler(new Error('Gerätespeicher antwortet nicht')), 8000);
      const t = d.transaction(STORE, modus);
      const store = t.objectStore(STORE);
      let ergebnis;
      arbeit(store, (r) => { ergebnis = r; });
      t.oncomplete = () => { clearTimeout(zeit); ok(ergebnis); };
      t.onerror = () => { clearTimeout(zeit); fehler(t.error); };
      t.onabort = () => { clearTimeout(zeit); fehler(t.error || new Error('Abgebrochen')); };
    });
  } catch (e) {
    verbindung = null;
    if (nochmal) return tx(modus, arbeit, false);
    throw e;
  }
}

function melden(protokollId) {
  try { window.dispatchEvent(new CustomEvent(EREIGNIS, { detail: { protokollId } })); } catch { /* egal */ }
}
export function beiFotoAenderung(cb) {
  const h = (e) => cb(e.detail || {});
  window.addEventListener(EREIGNIS, h);
  return () => window.removeEventListener(EREIGNIS, h);
}

// Alle Fotos im Geraet (wartende und abgelehnte). Wirft, wenn das Geraet sie
// nicht lesen kann - dann lieber "unbekannt" als faelschlich "0".
export async function alleWartenden() {
  return (await tx('readonly', (store, setze) => {
    const req = store.getAll();
    req.onsuccess = () => setze(req.result || []);
  })) || [];
}
// kategorie (seit 21.09.2026, Migration 63): Fotos ohne Pruefpunkt, z. B.
// 'typenschild_ms', 'schaltplan_ms', 'typenschild_trafo:<id>'.
export async function wartendeFuerPruefpunkt(pruefpunktId, kategorie) {
  return (await alleWartenden()).filter(f => (kategorie
    ? f.kategorie === kategorie
    : f.pruefpunktId === pruefpunktId && !f.kategorie));
}
// { wartend, abgelehnt } fuer ein Protokoll (oder alle).
export async function fotoZaehler(protokollId) {
  const alle = (await alleWartenden()).filter(f => !protokollId || f.protokollId === protokollId);
  return { wartend: alle.filter(f => !f.fehler).length, abgelehnt: alle.filter(f => f.fehler).length };
}

// Foto merken. blob ist schon verkleinert. Ergebnis: der Eintrag.
export async function fotoVormerken(blob, { stationId, protokollId, pruefpunktId, kategorie, titel, dateiname, userId }) {
  if (!stationId) throw new Error('stationId fehlt');
  const id = crypto.randomUUID();
  const eintrag = { id, blob, stationId, protokollId: protokollId || null, pruefpunktId: pruefpunktId || null,
    kategorie: kategorie || null,
    titel: titel || '', dateiname: dateiname || (id + '.jpg'), userId: userId || null, zeit: Date.now(), fehler: null };
  await tx('readwrite', (store) => { store.put(eintrag); });
  melden(protokollId);
  return eintrag;
}

export async function fotoVerwerfen(id) {
  let protokollId = null;
  await tx('readwrite', (store) => {
    const req = store.get(id);
    req.onsuccess = () => { if (req.result) { protokollId = req.result.protokollId; store.delete(id); } };
  });
  melden(protokollId);
}

async function nochImGeraet(id) {
  return !!(await tx('readonly', (store, setze) => {
    const req = store.get(id);
    req.onsuccess = () => setze(req.result || null);
  }));
}

// Sendet EIN gemerktes Foto. Ergebnis: 'gesendet' | 'wartet' | 'abgelehnt'.
async function fotoSenden(e) {
  if (!navigator.onLine) return 'wartet';
  const path = `stationen/${e.stationId}/${e.id}.jpg`;
  try {
    // Kein upsert: Die Speicher-Regeln erlauben nur Anlegen, nicht Ueberschreiben.
    // Gibt es die Datei schon, kam ein frueherer Versuch an - der Pfad enthaelt
    // die im Geraet erzeugte ID, sie kann nur von uns sein.
    const { error: upErr } = await withTimeout(
      supabase.storage.from('fotos').upload(path, e.blob, { upsert: false, contentType: 'image/jpeg' }),
      120000, 'Foto hochladen');
    if (upErr) {
      const text = String(upErr.message || '') + ' ' + String(upErr.statusCode || upErr.status || '');
      if (!/already exists|duplicate|409/i.test(text)) throw upErr;
    }
    const { data: urlData } = supabase.storage.from('fotos').getPublicUrl(path);
    const { error } = await withTimeout(supabase.from('protokoll_fotos').insert({
      id: e.id, station_id: e.stationId, protokoll_id: e.protokollId, pruefpunkt_id: e.pruefpunktId,
      // Nur mitschicken, wenn gesetzt - Fotos ohne Kategorie gehen so auch ohne Spalte.
      ...(e.kategorie ? { kategorie: e.kategorie } : {}),
      titel: e.titel || null, dateiname: e.dateiname, storage_path: path, public_url: urlData.publicUrl,
      in_pdf: true, erstellt_von_id: e.userId,
    }), 30000, 'Foto eintragen');
    // 23505: Eintrag gibt es schon (frueherer Versuch kam an) - erledigt.
    if (error && error.code !== '23505') throw error;

    // Hat der Monteur das Foto waehrend des Hochladens verworfen? Dann auch
    // auf dem Server wieder entfernen.
    if (!(await nochImGeraet(e.id))) {
      try {
        await supabase.from('protokoll_fotos').delete().eq('id', e.id);
        await supabase.storage.from('fotos').remove([path]);
      } catch { /* bleibt im Zweifel stehen - das Buero kann es loeschen */ }
      melden(e.protokollId);
      return 'gesendet';
    }
    await tx('readwrite', (store) => { store.delete(e.id); });
    melden(e.protokollId);
    return 'gesendet';
  } catch (err) {
    if (istNetzproblem(err) || /jwt|token/i.test(String(err?.message || ''))) return 'wartet';
    const text = String(err?.message || err);
    // Nur zurueckschreiben, wenn das Foto noch da ist (nicht inzwischen verworfen).
    await tx('readwrite', (store) => {
      const req = store.get(e.id);
      req.onsuccess = () => { if (req.result) store.put({ ...req.result, fehler: text }); };
    }).catch(() => {});
    melden(e.protokollId);
    console.error('[Fotos] vom Server abgelehnt:', e.id, err);
    return 'abgelehnt';
  }
}

let laufend = null;
let erneutGewuenscht = false;
// Alle wartenden Fotos senden. erneut = true nimmt auch abgelehnte mit - laeuft
// gerade ein Durchgang, folgt der erneute direkt danach.
export function fotosAbarbeiten({ erneut = false } = {}) {
  if (laufend) {
    if (erneut) erneutGewuenscht = true;
    return laufend;
  }
  laufend = (async () => {
    let gesendet = 0;
    let mitAbgelehnten = erneut;
    for (;;) {
      erneutGewuenscht = false;
      let liste = [];
      try { liste = await alleWartenden(); } catch { break; }
      for (const e of liste) {
        if (!navigator.onLine) break;
        if (e.fehler && !mitAbgelehnten) continue;
        const r = await fotoSenden(e);
        if (r === 'gesendet') gesendet++;
        if (r === 'wartet') break;
      }
      if (!erneutGewuenscht || !navigator.onLine) break;
      mitAbgelehnten = true;
    }
    return gesendet;
  })().finally(() => { laufend = null; });
  return laufend;
}

let gestartet = false;
export function starteFotoAutomatik() {
  if (gestartet) return;
  gestartet = true;
  window.addEventListener('online', () => { fotosAbarbeiten(); });
  setInterval(() => { if (navigator.onLine) fotosAbarbeiten(); }, 60000);
  fotosAbarbeiten();
}
